import store from "../store";
import { Channel, ChannelProtocol, ChannelTimer, ChannelExecuteDelay } from "./channel";
import { translate, showAlert, showNoResultsAlert, showServerTimeoutAlert, showException, EventHandler, Event } from "../../helpers";
import { Track } from "../../../components/common/track";
import { logFunnelEvent, getLandingRenderedAt } from "../../helpers/classes/funnel_tracking";

declare const gon: any;

// Toggle the searchfield overlay with the current search type text.
function searchfieldOverlay(hide: boolean): void {
  const query: string = `.search_box_overlay, \
                         .search_box_overlay_top, \
                         .search_box_overlay_top .${gon.search.search_type}`;

  if (hide) {
    $(query).hide();
  } else {
    $(query).show();
  }
}

export class SearchChannel extends Channel {
  public readonly onReceivedPacket: EventHandler<Event> = new EventHandler<Event>();

  private statusTimer: ChannelTimer;
  private itinerariesTimer: ChannelTimer;
  private filterRunDelay: ChannelExecuteDelay;
  private hasReceivedItineraries: boolean = false;
  private searchInitiatedAt: number;

  // Guards against double-firing the "first connection" tracking below if a
  // WebSocket handshake lands right around the 250ms HTTP-fallback boundary
  // in Channel's constructor (fallback already switched to HTTP and called
  // onConnected, then the WS handshake completes a moment later and calls
  // it again on the upgrade). onConnected still runs its protocol-specific
  // branch (the HTTP status() kick-off) every time; only the one-time setup
  // is guarded.
  private hasConnected: boolean = false;

  // Delay (ms) before each subsequent "status" re-poll while a search is still
  // running, growing over time instead of a flat 1s forever. Most searches finish
  // within the first couple of polls, so those stay at 1s (no change in perceived
  // speed); a search that runs long backs off to 2s then 3s, roughly halving the
  // total request volume for the slow cases -- the ones most likely to be piling
  // up concurrently on the server. The 30s overall statusTimer (server-timeout
  // alert) is unaffected by this and still governs the hard ceiling.
  private static readonly STATUS_POLL_DELAYS_MS = [1000, 1000, 1000, 2000, 2000, 3000];
  private statusPollAttempt: number = 0;

  constructor() {
    super("SearchChannel", { meta_test: gon.meta_test });

    this.filterRunDelay = new ChannelExecuteDelay(this, 200);

    this.statusTimer = new ChannelTimer((searchChannel: SearchChannel): void => {
      showServerTimeoutAlert("status");
    }, 30, this);

    // When the status timer is stopped it means that we have received "ready"
    this.statusTimer.onStop = () => {
      if (!this.hasReceivedItineraries) {
        this.itinerariesTimer = new ChannelTimer((searchChannel: SearchChannel): void => {
          showServerTimeoutAlert("itineraries");
        }, 10, this);
      }
    }

    searchfieldOverlay(false);

    // Set the search status because we now recieve the status call from the backend.
    store.commit("searchStatus", "searching");
  }

  public status(): void {
    this.perform("status");
  }

  /**
   * Run the filter.
   * @param filter
   */
  public runFilter(filter: any): void {
    if (store.state.searchStatus) {
      this.filterRunDelay.run(() => {
        this.perform("run_filter", {
          view_state: store.getters.viewState,
          filter: filter
        });
      });
    }
  }

  /**
   * Load more itineraries.
   * @param offset
   */
  public loadMoreResults(offset: number = 0): void {
    if (store.state.searchStatus) {
      this.perform("load_more", {
        offset: offset,
        view_state: store.getters.viewState,
        filter: $("#filter_form").serialize()
      });
    }
  }

  public runFilterData(): void {
    if (store.state.searchStatus) {
      this.perform("filter_data", {
        view_state: store.getters.viewState
      });
    }
  }

  protected getCableUrlQuery(): string {
    return `search_id=${gon.search.id}`;
  }

  protected onConnected(protocol: ChannelProtocol): void {
    // This used to only run for `protocol === Http`, which meant a
    // successful WebSocket connection never recorded searchInitiatedAt or
    // fired the "search_request_initiated" funnel event, and consequently
    // the "search_results_rendered" event later (which requires
    // searchInitiatedAt) never fired either. Track this regardless of which
    // protocol we ended up on, but only once.
    if (!this.hasConnected) {
      this.hasConnected = true;
      this.searchInitiatedAt = Date.now();

      const landingRenderedAt = getLandingRenderedAt();
      if (landingRenderedAt) {
        logFunnelEvent("search_request_initiated", {
          search_id: gon.search.id,
          duration_ms: this.searchInitiatedAt - landingRenderedAt
        });
      }
    }

    // Over HTTP we have to kick off the first status poll ourselves. Over
    // WebSocket, subscribing to the channel already triggers the equivalent
    // flow server-side (SearchChannel#subscribed -> start_search), so no
    // explicit first request is needed here.
    if (protocol === ChannelProtocol.Http) {
      this.status();
    }
  }

  protected onDisconnected(): void {

  }

  protected onActionFailed(action: string): void {
    // HTTP retries for this action (run_filter/load_more/filter_data/status)
    // are exhausted. Previously this was only logged (Channel base class),
    // leaving the UI stuck with no feedback and no way for the user to know
    // their action was lost.
    showException("search-channel-action-failed");
  }

  protected onReceived(resp: any): boolean {
    switch (resp.action) {
      case "status": {
        store.commit("searchStatus", resp.items);

        if (resp.items === "ready") {
          searchfieldOverlay(true);
        }

        this.statusTimer.restart();

        if (resp.items === "ready") {
          if (this.getProtocol() === ChannelProtocol.WebSocket) {
            this.statusTimer.stop();
          } else {
            this.statusTimer.stop();
            this.perform("filter_data");
            this.perform("run_filter");
          }
        } else if (resp.items === "error") {
          if (resp.search_error) {
            showAlert(
              "status-response",
              translate("Något är fel!"),
              resp.search_error,
              "/");
          } else {
            showNoResultsAlert("status-response", resp.youth);
          }
        } else {
          const self = this;
          const delayIndex = Math.min(this.statusPollAttempt, SearchChannel.STATUS_POLL_DELAYS_MS.length - 1);
          const delay = SearchChannel.STATUS_POLL_DELAYS_MS[delayIndex];
          this.statusPollAttempt++;
          setTimeout(() => self.status(), delay);
        }

        break;
      }

      case "itineraries": {
        this.setItineraries("updateItineraries", resp.items);
        break;
      }

      case "loadMoreItineraries": {
        store.commit("updateItineraries", { type: "append", data: resp.items });
        break;
      }

      case "filter_data": {
        store.commit("updateFilterData", resp.items);
        break;
      }

      case "redirect": {
        location.href = resp.url;
        break;
      }

      case "error": {
        // Broadcast by the server when run_filter/load_more/filter_data/
        // start_search raised on the SearchChannel and had nothing else to
        // send back. Previously the exception was only logged server-side
        // (pass_exception) and the client just kept waiting for a response
        // that would never arrive. Surface something to the user instead of
        // a silent, permanent stall.
        showException("search-channel-error");
        break;
      }

      default:
        this.onReceivedPacket.trigger(new Event(resp.action, resp.items));
        return false;
    }
    return true;
  }

  private setItineraries(type: string, payload: any): void {
    const isFirstResult: boolean = !this.hasReceivedItineraries;
    this.hasReceivedItineraries = true;
    if (this.itinerariesTimer) {
      this.itinerariesTimer.stop();
    }

    store.commit(type, { type: "set", data: payload });

    if (isFirstResult && this.searchInitiatedAt) {
      logFunnelEvent("search_results_rendered", {
        search_id: gon.search.id,
        duration_ms: Date.now() - this.searchInitiatedAt
      });
    }
  }
};
